import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router";

import { yupResolver } from "@hookform/resolvers/yup";

import { store } from "@src/index";
import { ISponsor } from "@src/types/api";

import FileUpload from "@components/FileUpload";

import { globalActions, selectSponsor } from "@features/global/globalSlice";

import { useAppSelector } from "@hooks/reduxHooks";

import { SPONSOR_LOGO_URL, VALID_FILE_EXTENSIONS } from "@lib/globals";
import { displayFormAlert } from "@lib/utils";

import DeleteModal from "@SiteManagement/Advertisers/components/DeleteModal";
import useFormHandlers from "@SiteManagement/Advertisers/hooks/useFormHandlers";
import { advertiserFormSchema } from "@SiteManagement/Advertisers/lib/advertiserFormSchema";
import { AdvertiserInfoData } from "@SiteManagement/Advertisers/types/advertisers";

export const loader = async ({ params }: any) => {
    const promises = [];
    const sponsors = store.getState().global.sponsors;

    !sponsors.length &&
        promises.push(store.dispatch(globalActions.getSponsors()));

    await Promise.all(promises);

    return params.advertiserId || null;
};

const Advertiser = (): React.JSX.Element => {
    const params = useParams();
    const advertiserId = parseInt(params.advertiserId || "0");
    const location = useLocation();
    const [logo, setLogo] = useState<File[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [sponsor, setSponsor] = useState<ISponsor>(
        useAppSelector(state => selectSponsor(state, advertiserId)) as ISponsor,
    );
    const allowedExtensions = VALID_FILE_EXTENSIONS.image.slice();

    useEffect(() => {
        const sponsor = selectSponsor(store.getState(), advertiserId);
        sponsor && setSponsor(sponsor);
    }, [location.pathname]);

    const methods = useForm<AdvertiserInfoData>({
        mode: "onChange",
        resolver: yupResolver(advertiserFormSchema),
        defaultValues: {
            id: sponsor?.id,
            name: sponsor?.name,
            link: sponsor?.link,
        },
    });

    const { handleUpdate, handleCreate, handleDelete } = useFormHandlers(
        setSponsor,
        setLogo,
        methods,
    );

    const onSubmit: SubmitHandler<AdvertiserInfoData> = async data => {
        if (params.advertiserId) {
            handleUpdate(data, logo);
        } else {
            handleCreate(data, logo);
        }
    };

    return (
        <Row>
            <Col lg={4}>
                {sponsor?.logo ? (
                    <img
                        src={`${SPONSOR_LOGO_URL}${sponsor?.logo}`}
                        alt={`${sponsor?.name} logo`}
                        className="sponsorLogo img-fluid"
                    />
                ) : (
                    "No Image Uploaded"
                )}
            </Col>
            <Col lg={8}>
                <FormProvider {...methods}>
                    <Form onSubmit={methods.handleSubmit(onSubmit)}>
                        <Row>
                            <Col>
                                <Form.Group controlId="name">
                                    <Form.Label className="mt-0">
                                        Advertiser Name:
                                    </Form.Label>
                                    <Form.Control
                                        {...methods.register("name")}
                                        defaultValue={sponsor?.name}
                                        isInvalid={
                                            methods.formState.errors.name
                                                ? true
                                                : false
                                        }
                                    />
                                    {methods.formState.errors.name &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: methods.formState.errors.name
                                                ?.message as string,
                                        })}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group controlId="link">
                                    <Form.Label>
                                        Advertiser Link (optional):
                                    </Form.Label>
                                    <Form.Control
                                        {...methods.register("link")}
                                        defaultValue={sponsor?.link}
                                        isInvalid={
                                            methods.formState.errors.link
                                                ? true
                                                : false
                                        }
                                    />
                                    {methods.formState.errors.link &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: methods.formState.errors.link
                                                ?.message as string,
                                        })}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Label>Advertiser Logo</Form.Label>
                                <Form.Group controlId="logo">
                                    <FileUpload
                                        maxFileCount={1}
                                        allowedFileExtensions={
                                            allowedExtensions
                                        }
                                        maxFileSize={5120000}
                                        fileList={logo}
                                        setFileList={setLogo}
                                    />
                                    <Form.Label className="small">
                                        Accepted file types: jpg, jpeg, png
                                        <br />
                                        Max file size: 5 MB
                                    </Form.Label>
                                    <p className="small mb-0">
                                        Upload a new logo to replace current one
                                    </p>
                                </Form.Group>
                                {methods.formState.errors.logo &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.logo
                                            ?.message as string,
                                    })}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Button type="submit">
                                    {params.advertiserId ? "Save" : "Add"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="danger"
                                    className="mx-2"
                                    onClick={() => setShowModal(true)}
                                >
                                    Delete
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </FormProvider>
            </Col>
            <DeleteModal
                show={showModal}
                setShow={setShowModal}
                handleDelete={handleDelete}
                sponsor={sponsor}
            />
        </Row>
    );
};

export default Advertiser;
